import { Box } from '@mui/system';
import { DrawerHeader } from '../../../../../drawer/DrawerHeader';
import { NameInput } from '../../../../../form/NameInput';
import { Grid, Stack, Typography, useTheme } from '@mui/material';
import {
	AccountCircleOutlined,
	CheckCircleOutlined,
	InsertInvitationOutlined,
	Link,
	SellOutlined,
	WarningAmberOutlined,
} from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import { PrioritySelect } from '../../../../../actions/inputs/PrioritySelect';
import { ActionPriority, ActionStatus, ActionUpdateOwnerEntityRelationInput } from '../../../../../gql/graphql';
import { DatePicker } from '../../../../../form/DatePicker';
import { StatusSelect } from '../../../../../actions/inputs/StatusSelect';
import { MemberSelect } from '../../../../../form/MemberSelect';
import { LinkType, LinksFormInput, isOldLinksJson } from '../../../../../form/links/LinksFormInput';
import { TagsSelect } from '../../../../../form/TagsSelect';
import { FC, useMemo } from 'react';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { isLinksJson } from '../../../../../form/links/LinksFormInput';
import { determineArrayUpdateOperation } from '../../../../../utils/determineArrayUpdateOperation';
import { graphql } from '../../../../../gql';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import dayjs from 'dayjs';
import { useSnackbar } from '../../../../../store/snackbar';

const actionDrawerForm_getAction = graphql(`
	query actionDrawerForm_getAction($actionId: UUID!) {
		getAction(by: { id: $actionId }) {
			name
			deadline
			status
			priority
			owner {
				id
			}
			links
			tags {
				id
			}
			createdAt
		}
	}
`);

const actionDrawerForm_updateAction = graphql(`
	mutation actionDrawerForm_updateAction($actionId: UUID!, $data: ActionUpdateInput!) {
		updateAction(by: { id: $actionId }, data: $data) {
			ok
			errorMessage
		}
	}
`);

// NOTE: Create multiple instances of the same mutation to separate loading states of input components.
const useUpdateAction = () => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	return useApiMutation(actionDrawerForm_updateAction, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.updateAction.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'action.detail.update.success' }),
					error: intl.formatMessage({ id: 'action.detail.update.error' }),
				},
				errorMessage: result.updateAction.errorMessage,
				openSnackbar,
			}),
	});
};

type ActionDrawerFormProps = Readonly<{
	actionId: string;
}>;

export const ActionDrawerForm: FC<ActionDrawerFormProps> = ({ actionId }) => {
	const intl = useIntl();
	const theme = useTheme();

	const {
		data: action,
		isLoading: isActionLoading,
		isPending: isActionPending,
	} = useApiQuery(actionDrawerForm_getAction, {
		variables: { actionId },
	});

	const { mutate: updateName } = useUpdateAction();
	const { mutate: updatePriority, isPending: isUpdatePriorityPending } = useUpdateAction();
	const { mutate: updateDeadline, isPending: isUpdateDeadlinePending } = useUpdateAction();
	const { mutate: updateOwner, isPending: isUpdateOwnerPending } = useUpdateAction();
	const { mutate: updateLinks } = useUpdateAction();
	const { mutate: updateTags } = useUpdateAction();
	const { mutate: updateStatus, isPending: isUpdateStatusPending } = useUpdateAction();

	const handleUpdateName = useMemo(
		() => (newName: string) => {
			updateName({ actionId, data: { name: newName } });
		},
		[actionId, updateName],
	);

	const handleUpdatePriority = (newPriority: ActionPriority) => {
		updatePriority({ actionId, data: { priority: newPriority } });
	};

	const handleUpdateDeadline = (newDeadline: string | null) => {
		updateDeadline({ actionId, data: { deadline: newDeadline } });
	};

	const handleUpdateOwner = (newOwnerId: string) => {
		if (newOwnerId !== '' && newOwnerId === action?.getAction?.owner?.id) {
			return;
		}

		let data: ActionUpdateOwnerEntityRelationInput;
		if (newOwnerId === '') {
			data = { disconnect: true };
		} else {
			data = { connect: { id: newOwnerId } };
		}

		if (!isActionPending) {
			updateOwner({ actionId, data: { owner: data } });
		}
	};

	const links = (() => {
		if (isOldLinksJson(action?.getAction?.links)) {
			return action.getAction.links.values.map((link) => ({ link, text: undefined }));
		} else if (isLinksJson(action?.getAction?.links)) {
			return action.getAction.links.values;
		}
		return null;
	})();

	const handleUpdateLinks = (newLinks: LinkType[]) => {
		if (!isActionPending) {
			updateLinks({ actionId, data: { links: { values: newLinks } } });
		}
	};

	const handleUpdateTags = (newTags: string[]) => {
		const tagsOperations = determineArrayUpdateOperation(action?.getAction?.tags.map(({ id }) => id) ?? [], newTags);
		// Prevent firing empty update mutation
		if (tagsOperations.length === 0) {
			return;
		}

		if (!isActionPending) {
			updateTags({ actionId, data: { tags: tagsOperations } });
		}
	};

	const handleUpdateStatus = (newStatus: ActionStatus) => {
		if (!isActionPending) {
			updateStatus({
				actionId,
				data: { status: newStatus, solvedAt: newStatus === 'resolved' ? dayjs().format() : null },
			});
		}
	};

	return (
		<>
			<Box px={3}>
				<DrawerHeader />
				<NameInput
					value={action?.getAction?.name}
					isLoading={isActionPending}
					memoizedOnChange={handleUpdateName}
					placeholder={intl.formatMessage({ id: 'action.detail.name.placeholder' })}
				/>
			</Box>
			<Grid container alignItems={'center'} spacing={0.5} px={3} py={2}>
				<Grid item xs={4}>
					<Stack direction={'row'} gap={1} alignItems={'center'}>
						<WarningAmberOutlined sx={{ fill: theme.palette.secondary.main, fontSize: 18 }} />
						<Typography variant={'h6'} color={'secondary'}>
							<FormattedMessage id={'action.detail.priority.label'} />
						</Typography>
					</Stack>
				</Grid>
				<Grid item xs={8}>
					<PrioritySelect
						loading={isActionPending || isUpdatePriorityPending}
						value={action?.getAction?.priority}
						onChange={({ target: { value } }) => handleUpdatePriority(value as ActionPriority)}
					/>
				</Grid>
				<Grid item xs={4}>
					<Stack direction={'row'} gap={1} alignItems={'center'}>
						<InsertInvitationOutlined sx={{ fill: theme.palette.secondary.main, fontSize: 18 }} />
						<Typography variant={'h6'} color={'secondary'}>
							<FormattedMessage id={'action.detail.deadline.label'} />
						</Typography>
					</Stack>
				</Grid>
				<Grid item xs={8}>
					<DatePicker
						value={action?.getAction?.deadline ?? null}
						onChange={handleUpdateDeadline}
						disabled={isActionPending || isUpdateDeadlinePending}
						size={'middle'}
					/>
				</Grid>
				<Grid item xs={4}>
					<Stack direction={'row'} gap={1} alignItems={'center'}>
						<CheckCircleOutlined sx={{ fill: theme.palette.secondary.main, fontSize: 18 }} />
						<Typography variant={'h6'} color={'secondary'}>
							<FormattedMessage id={'action.detail.status.label'} />
						</Typography>
					</Stack>
				</Grid>
				<Grid item xs={8}>
					<StatusSelect
						loading={isActionPending || isUpdateStatusPending}
						value={action?.getAction?.status}
						onChange={({ target: { value } }) => handleUpdateStatus(value as ActionStatus)}
					/>
				</Grid>
				<Grid item xs={4}>
					<Stack direction={'row'} gap={1} alignItems={'center'}>
						<AccountCircleOutlined sx={{ fill: theme.palette.secondary.main, fontSize: 18 }} />
						<Typography variant={'h6'} color={'secondary'}>
							<FormattedMessage id={'action.detail.owner.label'} />
						</Typography>
					</Stack>
				</Grid>
				<Grid item xs={8}>
					<MemberSelect
						loading={isActionPending || isUpdateOwnerPending}
						value={action?.getAction?.owner?.id ?? ''}
						onChange={({ target: { value } }) => handleUpdateOwner(value)}
						size={'small'}
					/>
				</Grid>
				<Grid item xs={4} alignSelf={'flex-start'}>
					<Stack direction={'column'} justifyContent={'center'} sx={{ height: 40 }}>
						<Stack direction={'row'} gap={1} alignItems={'center'}>
							<Link sx={{ fill: theme.palette.secondary.main, fontSize: 18 }} />
							<Typography variant={'h6'} color={'secondary'}>
								<FormattedMessage id={'action.detail.links.label'} />
							</Typography>
						</Stack>
					</Stack>
				</Grid>
				<Grid item xs={8}>
					<LinksFormInput isLoading={isActionLoading} value={links} onChange={handleUpdateLinks} maxNumberOfLinks={10} size={'small'} />
				</Grid>
				<Grid item xs={4} alignSelf={'flex-start'}>
					<Stack direction={'column'} justifyContent={'center'} sx={{ height: 37 }}>
						<Stack direction={'row'} gap={1} alignItems={'center'}>
							<SellOutlined sx={{ fill: theme.palette.secondary.main, fontSize: 18 }} />
							<Typography variant={'h6'} color={'secondary'}>
								<FormattedMessage id={'action.detail.tags.label'} />
							</Typography>
						</Stack>
					</Stack>
				</Grid>
				<Grid item xs={8}>
					<TagsSelect
						loading={isActionPending}
						value={action?.getAction?.tags.map(({ id }) => id) ?? []}
						onChange={handleUpdateTags}
						size={'small'}
					/>
				</Grid>
			</Grid>
		</>
	);
};
